import { ApiError, inspectedFetch, type ApiResponse } from '../../core/index.js'
import type { BridgeCallout, OpenBridgeCalloutInput } from '../../schedule/index.js'
import type { DemoFacility, DemoWorker } from './demoData.js'
import type { DemoPlan } from './demoPlan.js'
import { isBlankPlan } from './demoRules.js'

/** How far ahead the demo's Clinical Bridge day is: a week, so the callout is open and the worker's calendar shows the day. */
const BRIDGE_DAYS_AHEAD = 7
const DAY_MS = 24 * 60 * 60 * 1000

/** The shift the demo's callout needs covering: a day shift on the facility's first unit. */
export const DEMO_BRIDGE_SHIFT = { start: '07:00', end: '15:00', unitId: 'unit-a', unitName: 'Unit A' } as const
export const DEMO_BRIDGE_REASON = 'Called out sick'
export const DEMO_BRIDGE_RATE = 24

/**
 * The Clinical Bridge callout a demo opens with (plan section 7): at the
 * first active facility, for its first worker - the one marked eligible and
 * handed a login on the outcome screen - so the worker's seat opens on a
 * live "Open shift" card and the dashboard on a pool waiting. Null for a
 * blank demo.
 */
export function demoBridge(plan: DemoPlan): { facility: DemoFacility; worker: DemoWorker } | null {
  if (isBlankPlan(plan)) {
    return null
  }
  const facility = plan.activeFacilities[0]
  const worker = facility ? plan.workers.find((candidate) => candidate.facilityId === facility.facilityId) : undefined
  return facility && worker ? { facility, worker } : null
}

/** The day the callout is for, `YYYY-MM-DD` in the facility's zone, a week from now. */
export function demoBridgeDate(facility: Pick<DemoFacility, 'timezone'>, now: Date): string {
  return new Date(now.getTime() + BRIDGE_DAYS_AHEAD * DAY_MS).toLocaleDateString('en-CA', { timeZone: facility.timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
}

export interface FacilityAdministratorSession {
  accessToken: string
}

/** `GET /auth/facility-login` with the login the demo created: only a facility administrator opens a callout. */
export async function loginAsFacilityAdministrator(apiBaseUrl: string, email: string, password: string): Promise<FacilityAdministratorSession> {
  const response = await inspectedFetch(`${apiBaseUrl}/auth/facility-login`, { headers: { Authorization: `Basic ${btoa(`${email}:${password}`)}` } })
  let body: ApiResponse<{ accessToken: string }> | null = null
  try {
    body = (await response.json()) as ApiResponse<{ accessToken: string }>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !body?.success || !body.data?.accessToken) {
    throw new ApiError(body?.error ?? `The facility administrator login for ${email} failed (${response.status})`, response.status)
  }
  return { accessToken: body.data.accessToken }
}

/** `POST /facilities/{facilityId}/bridge/callouts` as the facility administrator. The pool is told at once. */
export async function openCalloutAsAdministrator(apiBaseUrl: string, session: FacilityAdministratorSession, facilityId: string, input: OpenBridgeCalloutInput): Promise<BridgeCallout> {
  const response = await inspectedFetch(`${apiBaseUrl}/facilities/${encodeURIComponent(facilityId)}/bridge/callouts`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })
  let body: ApiResponse<BridgeCallout> | null = null
  try {
    body = (await response.json()) as ApiResponse<BridgeCallout>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !body?.success || body.data === undefined) {
    throw new ApiError(body?.error ?? `Opening the Clinical Bridge callout failed (${response.status})`, response.status)
  }
  return body.data
}
